import { apiConnection } from "@/services/api-connection";
import { useNotificationStore } from "@/store/notifications/notification";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { fileManager } from "@/services/file/file-manager";

class MessengerMessageManager {
  intervals = {};
  initialPingDelays = {};
  pingFailures = {};
  loading = false;

  MAX_PING_FAILURES = 3;
  LIST_PAGE_SIZE = 50;

  PING_PHASES = [
    { upTo: 0.5 * 60 * 1000, delay: 10 * 500 }, // 0–30 s  : every 5s
    { upTo: 2 * 60 * 1000, delay: 10 * 1000 }, // 30s–2 min  : every 10s
    { upTo: 5 * 60 * 1000, delay: 30 * 1000 }, // 2–5 min  : every 30s
    { upTo: 15 * 60 * 1000, delay: 60 * 1000 }, // 5–15 min : every 60s
    { upTo: 60 * 60 * 1000, delay: 2 * 60 * 1000 }, // 15–60 min : every 2 min
    // Borne alignée sur redeliver_timeout: 86400 du transport async_slow (config/packages/messenger.yaml côté back),
    // pas allongé après 4 h pour limiter le volume de requêtes par onglet ouvert.
    { upTo: 4 * 60 * 60 * 1000, delay: 5 * 60 * 1000 }, // 1–4 h : every 5 min
    { upTo: 24 * 60 * 60 * 1000, delay: 15 * 60 * 1000 }, // 4–24 h : every 15 min
  ];

  STATUS_WAITING = "waiting";
  STATUS_PROCESSING = "processing";
  STATUS_FAILED = "failed";
  STATUS_ERROR = "error";
  STATUS_SUCCESS = "success";
  STATUS_OFF = "off";
  STATUS_UNKNOWN = "unknown";

  startPing = (messageId, open = true, initialPingDelay = null) => {
    const store = useNotificationStore();
    const messageAlreadyPinging = Object.values(store.messages).find((message) => message.id === messageId);
    if (!messageAlreadyPinging) {
      // initialPingDelay is only recorded when the ping sequence for this messageId
      // actually starts here. If startPing is called again while a ping is already
      // in progress for the same messageId, this branch is skipped and the delay is
      // intentionally left untouched: the initial delay only makes sense at the very
      // start of the polling sequence, it cannot be changed mid-flight.
      if (initialPingDelay) {
        this.initialPingDelays[messageId] = initialPingDelay;
      }
      const message = {
        id: messageId,
        status: this.STATUS_WAITING,
        totalIterations: null,
        currentIterations: null,
        label: i18n.global.t("header.notification.waiting"),
        fileTypeIcon: "file",
        icon: "pause",
      };
      store.setStatus(this.STATUS_WAITING);
      store.setOpen(open);
      store.addMessage(message);
      store.setLastPingedMessage(message);
      this.ping(messageId).then((res) => {
        if (res && !res.success && !res.aborted) {
          this.scheduleNextPing(messageId);
        }
      });
    }
  };

  processMessage = (res, initialPingDelay = null) => {
    if (res.messageId) {
      this.startPing(res.messageId, true, initialPingDelay);
    } else if (!res.success && res.message) {
      notificationManager.showAlert("warning", i18n.global.t(res.message));
    } else {
      notificationManager.showAlert("error", i18n.global.t("error_file"));
    }
  };

  getDelayForElapsed = (elapsed, messageId = null) => {
    const [firstPhase, ...otherPhases] = this.PING_PHASES;
    if (elapsed < firstPhase.upTo) {
      return this.initialPingDelays[messageId] || firstPhase.delay;
    }
    for (const phase of otherPhases) {
      if (elapsed < phase.upTo) {
        return phase.delay;
      }
    }
    return null; // Beyond 24 hours: stop
  };

  scheduleNextPing = (messageId) => {
    const store = useNotificationStore();
    const message = store.messages.find((m) => m.id === messageId);
    const date = message?.date ? new Date(message.date).getTime() : null;
    const elapsed = date ? Date.now() - date : 0;
    const delay = this.getDelayForElapsed(elapsed, messageId);

    // Budget épuisé : le client arrête d'observer, il ne conclut pas à un échec, le worker peut encore tourner.
    if (delay === null) {
      this.stopInterval(messageId);
      const unknownMessage = { ...message, status: this.STATUS_UNKNOWN, icon: "circle-question" };
      store.updateMessage(unknownMessage);
      store.setStatus(this.STATUS_UNKNOWN);
      store.setLastPingedMessage(unknownMessage);
      return;
    }

    this.intervals[messageId] = setTimeout(() => {
      this.ping(messageId).then((res) => {
        if (res && !res.success && !res.aborted) {
          this.scheduleNextPing(messageId);
        }
      });
    }, delay);
  };

  ping = (messageId) => {
    if (!messageId) {
      return Promise.resolve({ success: false });
    }

    return new Promise((resolve) => {
      const store = useNotificationStore();
      this.loading = true;
      apiConnection
        .get("/app/messenger-monitoring/" + messageId)
        .then((res) => {
          delete this.pingFailures[messageId];
          store.setLast(res);
          this.updateIfExist(res);
          if (
            res.status === this.STATUS_SUCCESS ||
            res.status === this.STATUS_FAILED ||
            res.status === this.STATUS_ERROR
          ) {
            this.stopInterval(messageId);
            if (
              res.action &&
              res.action.includes("/files/token/") &&
              !res.actionOptions?.routeFront &&
              res.actionOptions?.autoDownload
            ) {
              fileManager
                .getDownloadFileUrl(res.action)
                .then((response) => {
                  if (response && response.url) {
                    fileManager.downloadFileFromUrlWithToken(response.url);
                    notificationManager.showNotification("success", i18n.global.t("global.download.done"));
                  }
                })
                // L'erreur est déjà notifiée globalement par apiConnection : on absorbe
                // simplement le reject pour éviter une unhandled rejection (pas de second toast).
                .catch(() => {});
            }
            if (res.actionOptions && typeof res.actionOptions === "object" && res.actionOptions.refresh === true) {
              store.incrementRefreshComponentKey();
            }
            resolve({ success: true });
          } else {
            resolve({ success: false });
          }
        })
        .catch(() => {
          const aborted = this.registerPingFailure(messageId);
          resolve({ success: false, aborted });
          if (aborted) {
            // L'état d'erreur local est déjà posé par registerPingFailure : l'échec
            // d'expire n'a pas besoin d'être remonté, on absorbe juste le reject.
            this.expire(messageId).catch(() => {});
          }
        })
        .finally(() => {
          this.loading = false;
        });
    });
  };

  // Le message vient du back (données métier concaténées : libellé de mission, intitulé RNCP saisis
  // par un utilisateur) et est affiché tel quel via SweetAlert2, qui rend son `title` comme HTML : on
  // ne lui fait donc pas confiance sans vérifier son type, et le fallback i18n couvre tout ce qui n'est
  // pas une chaîne exploitable.
  resolveErrorMessage = (message, fallbackKey) => {
    const backMessage =
      typeof message?.actionParameters?.errorMessage === "string" ? message.actionParameters.errorMessage.trim() : "";
    return backMessage || i18n.global.t(fallbackKey);
  };

  registerPingFailure = (messageId) => {
    const store = useNotificationStore();
    this.pingFailures[messageId] = (this.pingFailures[messageId] ?? 0) + 1;
    if (this.pingFailures[messageId] < this.MAX_PING_FAILURES) {
      return false;
    }

    this.stopInterval(messageId);
    store.updateMessage({
      id: messageId,
      status: this.STATUS_ERROR,
      icon: "xmark",
      label: i18n.global.t("error.error_occured"),
    });
    // Statut global partagé par tous les messages trackés : un échec isolé (ex: report en
    // parallèle d'une sync) l'affiche brièvement en erreur ; se corrige seul au prochain ping réussi.
    store.setStatus(this.STATUS_ERROR);

    return true;
  };

  updateIfExist(res) {
    const store = useNotificationStore();
    const existingMessage = store.messages.find((m) => m.id === res.id);
    // Propagation générique : tous les champs renvoyés par le monitoring sont
    // fusionnés dans le message du store (updateMessage merge par id). Permet aux
    // consommateurs (ex: AdminOpcoView) de lire des champs métier additionnels
    // sans coupler ce manager à un contexte précis.
    store.updateMessage({ ...res });
    store.setStatus(res.status);
    if (!existingMessage || existingMessage.status !== res.status) {
      store.setLastPingedMessage({ ...existingMessage, ...res });
    }
  }

  getStatusTranslationKey = (status) => {
    switch (status) {
      case this.STATUS_WAITING:
      case this.STATUS_PROCESSING:
        return "header.notification.status_processing";
      case this.STATUS_SUCCESS:
        return "header.notification.status_success";
      case this.STATUS_FAILED:
      case this.STATUS_ERROR:
        return "header.notification.status_error";
      case this.STATUS_UNKNOWN:
        return "header.notification.status_unknown";
      default:
        return null;
    }
  };

  stopInterval = (messageId) => {
    if (this.intervals[messageId]) {
      clearTimeout(this.intervals[messageId]);
      this.intervals[messageId] = null;
    }
    delete this.initialPingDelays[messageId];
    delete this.pingFailures[messageId];
  };

  async loadListPage(append = false) {
    const store = useNotificationStore();
    const offset = append ? store.list.length : 0;
    const page = await apiConnection.get("/app/messenger-monitoring/list", {
      limit: this.LIST_PAGE_SIZE,
      offset,
    });
    if (append) {
      store.appendList(page);
    } else {
      store.setList(page);
    }
    store.setListHasMore(page.length >= this.LIST_PAGE_SIZE);
  }

  // Sans appelant applicatif, conservée pour un futur bouton d'annulation du suivi.
  expire(messageId) {
    return apiConnection.put("/app/messenger-monitoring/" + messageId + "/expire").then((res) => {
      this.updateIfExist(res);
    });
  }

  async resumePendingMessages() {
    const pending = await apiConnection.get("/app/messenger-monitoring/list", {
      status: [this.STATUS_WAITING, this.STATUS_PROCESSING],
    });
    const store = useNotificationStore();
    pending
      .filter((message) => [this.STATUS_WAITING, this.STATUS_PROCESSING].includes(message.status))
      .forEach((message) => {
        this.startPing(message.id, false);
        store.setStatus(this.STATUS_PROCESSING);
      });
  }
}

export const messengerMessageManager = new MessengerMessageManager();
